import React, { useCallback, useState, useMemo } from 'react';
import {
  Copy,
  Scissors,
  Trash2,
  Play,
  Power,
  PowerOff,
  Files,
  Palette,
  Type,
  AlignLeft,
  AlignCenter,
  AlignRight,
  Bold,
  Italic,
} from 'lucide-react';
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuTrigger,
  ContextMenuShortcut,
  ContextMenuSub,
  ContextMenuSubContent,
  ContextMenuSubTrigger,
} from '@/components/ui/context-menu';
import { useWorkflowStore } from '@/stores/workflow-store';
import { useCanvasOperations } from '@/hooks/useCanvasOperations';
import { toast } from 'sonner';
import { TestNodeDialog } from './TestNodeDialog';
import { getShortcuts } from '@/utils/keyboard';

interface ColorOption {
  value: string;
  label: string;
  class: string;
}

interface TextOption {
  value: string;
  label: string;
}

interface NodeContextMenuProps {
  children: React.ReactNode;
  nodeId: string;
  nodeType?: string;
  colorOptions?: ColorOption[];
  currentColor?: string;
  onColorChange?: (color: string) => void;
  textSizeOptions?: TextOption[];
  currentTextSize?: string;
  onTextSizeChange?: (size: string) => void;
  textAlignOptions?: TextOption[];
  currentTextAlign?: string;
  onTextAlignChange?: (align: string) => void;
  isBold?: boolean;
  onToggleBold?: () => void;
  isItalic?: boolean;
  onToggleItalic?: () => void;
  onDelete?: () => void;
}

export function NodeContextMenu({
  children,
  nodeId,
  nodeType,
  colorOptions,
  currentColor,
  onColorChange,
  textSizeOptions,
  currentTextSize,
  onTextSizeChange,
  textAlignOptions,
  currentTextAlign,
  onTextAlignChange,
  isBold,
  onToggleBold,
  isItalic,
  onToggleItalic,
  onDelete: customOnDelete,
}: NodeContextMenuProps) {
  const { nodes, updateNode, deleteNode } = useWorkflowStore();
  const { copyNodes, cutNodes, duplicateNodes } = useCanvasOperations();
  const [showTestDialog, setShowTestDialog] = useState(false);
  
  // Get shortcuts at runtime for proper OS detection
  const shortcuts = useMemo(() => getShortcuts(), []);

  const node = nodes.find((n) => n.id === nodeId);
  const isLabel = nodeType === 'label';

  const handleCopy = useCallback(() => {
    if (node) {
      copyNodes([node]);
      toast.success('Node copied');
    }
  }, [node, copyNodes]);

  const handleCut = useCallback(() => {
    if (node) {
      cutNodes([node]);
      toast.success('Node cut');
    }
  }, [node, cutNodes]);

  const handleDuplicate = useCallback(() => {
    if (node) {
      duplicateNodes([node]);
      toast.success('Node duplicated');
    }
  }, [node, duplicateNodes]);

  const handleDelete = useCallback(() => {
    if (customOnDelete) {
      customOnDelete();
    } else {
      deleteNode(nodeId);
    }
    toast.success('Node deleted');
  }, [nodeId, deleteNode, customOnDelete]);

  const handleToggleDisabled = useCallback(() => {
    if (node) {
      const isDisabled = node.data?.disabled || false;
      updateNode(nodeId, {
        ...node.data,
        disabled: !isDisabled,
      });
      toast.success(isDisabled ? 'Node enabled' : 'Node disabled');
    }
  }, [node, nodeId, updateNode]);

  const handleExecute = useCallback(() => {
    setShowTestDialog(true);
  }, []);

  const handleColorSelect = useCallback((color: string) => {
    if (onColorChange) {
      onColorChange(color);
      toast.success(`Color changed to ${color}`);
    }
  }, [onColorChange]);

  const handleTextSizeSelect = useCallback((size: string) => {
    if (onTextSizeChange) {
      onTextSizeChange(size);
    }
  }, [onTextSizeChange]);

  const handleTextAlignSelect = useCallback((align: string) => {
    if (onTextAlignChange) {
      onTextAlignChange(align);
    }
  }, [onTextAlignChange]);

  const getAlignIcon = (align: string) => {
    switch (align) {
      case 'center': return AlignCenter;
      case 'right': return AlignRight;
      default: return AlignLeft;
    }
  };

  if (!node) return <>{children}</>;

  const isDisabled = node.data?.disabled || false;

  return (
    <>
      <ContextMenu>
        <ContextMenuTrigger asChild>
          {children}
        </ContextMenuTrigger>
        <ContextMenuContent className="w-56">
          {/* Show formatting options for labels */}
          {isLabel && (
            <>
              {/* Color picker */}
              {colorOptions && (
                <ContextMenuSub>
                  <ContextMenuSubTrigger>
                    <Palette className="mr-2 h-4 w-4" />
                    Color
                  </ContextMenuSubTrigger>
                  <ContextMenuSubContent className="w-48">
                    {colorOptions.map((option) => (
                      <ContextMenuItem
                        key={option.value}
                        onClick={() => handleColorSelect(option.value)}
                      >
                        <div className={`mr-2 h-4 w-4 rounded ${option.class} border border-gray-300`} />
                        {option.label}
                        {currentColor === option.value && (
                          <span className="ml-auto text-xs">✓</span>
                        )}
                      </ContextMenuItem>
                    ))}
                  </ContextMenuSubContent>
                </ContextMenuSub>
              )}

              {/* Text size */}
              {textSizeOptions && (
                <ContextMenuSub>
                  <ContextMenuSubTrigger>
                    <Type className="mr-2 h-4 w-4" />
                    Text Size
                  </ContextMenuSubTrigger>
                  <ContextMenuSubContent className="w-48">
                    {textSizeOptions.map((option) => (
                      <ContextMenuItem
                        key={option.value}
                        onClick={() => handleTextSizeSelect(option.value)}
                      >
                        {option.label}
                        {currentTextSize === option.value && (
                          <span className="ml-auto text-xs">✓</span>
                        )}
                      </ContextMenuItem>
                    ))}
                  </ContextMenuSubContent>
                </ContextMenuSub>
              )}

              {/* Text alignment */}
              {textAlignOptions && (
                <ContextMenuSub>
                  <ContextMenuSubTrigger>
                    {React.createElement(getAlignIcon(currentTextAlign || 'left'), { className: 'mr-2 h-4 w-4' })}
                    Alignment
                  </ContextMenuSubTrigger>
                  <ContextMenuSubContent className="w-48">
                    {textAlignOptions.map((option) => {
                      const AlignIcon = getAlignIcon(option.value);
                      return (
                        <ContextMenuItem
                          key={option.value}
                          onClick={() => handleTextAlignSelect(option.value)}
                        >
                          <AlignIcon className="mr-2 h-4 w-4" />
                          {option.label}
                          {currentTextAlign === option.value && (
                            <span className="ml-auto text-xs">✓</span>
                          )}
                        </ContextMenuItem>
                      );
                    })}
                  </ContextMenuSubContent>
                </ContextMenuSub>
              )}

              {/* Bold toggle */}
              {onToggleBold && (
                <ContextMenuItem onClick={onToggleBold}>
                  <Bold className="mr-2 h-4 w-4" />
                  Bold
                  {isBold && <span className="ml-auto text-xs">✓</span>}
                </ContextMenuItem>
              )}

              {/* Italic toggle */}
              {onToggleItalic && (
                <ContextMenuItem onClick={onToggleItalic}>
                  <Italic className="mr-2 h-4 w-4" />
                  Italic
                  {isItalic && <span className="ml-auto text-xs">✓</span>}
                </ContextMenuItem>
              )}

              <ContextMenuSeparator />
            </>
          )}

          {/* Show Test Node only for non-sticky notes */}
          {!isLabel && (
            <>
              <ContextMenuItem 
                onClick={handleExecute}
                disabled={isDisabled}
              >
                <Play className="mr-2 h-4 w-4" />
                Test Node
                <ContextMenuShortcut>{shortcuts.execute}</ContextMenuShortcut>
              </ContextMenuItem>
              <ContextMenuSeparator />
            </>
          )}

          <ContextMenuItem onClick={handleCopy}>
            <Copy className="mr-2 h-4 w-4" />
            Copy
            <ContextMenuShortcut>{shortcuts.copy}</ContextMenuShortcut>
          </ContextMenuItem>
          <ContextMenuItem onClick={handleCut}>
            <Scissors className="mr-2 h-4 w-4" />
            Cut
            <ContextMenuShortcut>{shortcuts.cut}</ContextMenuShortcut>
          </ContextMenuItem>
          <ContextMenuItem onClick={handleDuplicate}>
            <Files className="mr-2 h-4 w-4" />
            Duplicate
            <ContextMenuShortcut>{shortcuts.duplicate}</ContextMenuShortcut>
          </ContextMenuItem>

          {/* Show Enable/Disable only for non-sticky notes */}
          {!isLabel && (
            <>
              <ContextMenuSeparator />
              <ContextMenuItem onClick={handleToggleDisabled}>
                {isDisabled ? (
                  <>
                    <Power className="mr-2 h-4 w-4" />
                    Enable
                  </>
                ) : (
                  <>
                    <PowerOff className="mr-2 h-4 w-4" />
                    Disable
                  </>
                )}
              </ContextMenuItem>
            </>
          )}

          <ContextMenuSeparator />
          <ContextMenuItem onClick={handleDelete} className="text-red-600">
            <Trash2 className="mr-2 h-4 w-4" />
            Delete
            <ContextMenuShortcut>{shortcuts.delete}</ContextMenuShortcut>
          </ContextMenuItem>
        </ContextMenuContent>
      </ContextMenu>
    
      {node && !isLabel && (
        <TestNodeDialog
          node={node}
          open={showTestDialog}
          onOpenChange={setShowTestDialog}
        />
      )}
    </>
  );
}
